import { useState } from "react";
import { Link } from "react-router-dom";
import { INDUSTRIES, INDUSTRY_CATEGORIES } from "../lib/industries";
import { DOMAINS } from "../lib/domains";
import { useTranslation } from "../lib/i18n";

interface MegaMenuProps {
  type: "industries" | "domains" | "certifications";
  onClose: () => void;
}

export function MegaMenu({ type, onClose }: MegaMenuProps) {
  const { t } = useTranslation();

  const panelStyle: React.CSSProperties = {
    position: "absolute",
    top: "var(--nav-height, 64px)",
    left: 0,
    right: 0,
    background: "var(--bg-dark, #0a0e14)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    padding: "1.75rem 0",
    zIndex: 100,
    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
  };

  if (type === "industries") {
    return (
      <div role="menu" aria-label="Industries mega menu" style={panelStyle}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 2rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "2rem" }}>
            {INDUSTRY_CATEGORIES.map((category) => (
              <div key={category}>
                <h4 style={{
                  fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.1em", color: "var(--accent-400)",
                  marginBottom: "0.875rem", paddingBottom: "0.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }} title={category}>
                  {category}
                </h4>
                <ul style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                  {INDUSTRIES.filter((i) => i.category === category).sort((a, b) => a.name.localeCompare(b.name)).map((industry) => (
                    <li key={industry.slug}>
                      <Link
                        to={`/industries/${industry.slug}`}
                        onClick={onClose}
                        style={{
                          display: "block", padding: "0.4rem 0.5rem",
                          fontSize: "0.8125rem", color: "var(--neutral-300)",
                          borderRadius: "var(--radius-sm)",
                          transition: "all var(--transition-fast)",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "var(--accent-400)";
                          e.currentTarget.style.background = "rgba(61,220,151,0.06)";
                          e.currentTarget.style.paddingLeft = "0.75rem";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "var(--neutral-300)";
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.paddingLeft = "0.5rem";
                        }}
                      >
                        {industry.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: "1.5rem", paddingTop: "1.25rem",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <Link to="/industries" onClick={onClose} style={{
              fontSize: "0.875rem", fontWeight: 600, color: "var(--accent-400)",
              display: "flex", alignItems: "center", gap: "0.375rem",
            }}>
              {t("megaMenu.viewAllIndustries")} &rarr;
            </Link>
            <span style={{ fontSize: "0.75rem", color: "var(--neutral-500)" }}>
              38 industries &middot; 12 safety domains &middot; 4 certification levels
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "certifications") {
    return (
      <div role="menu" aria-label="Certifications mega menu" style={panelStyle}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 2rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "2rem" }}>
            {INDUSTRY_CATEGORIES.map((category) => (
              <div key={category}>
                <h4 style={{
                  fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.1em", color: "var(--accent-400)",
                  marginBottom: "0.875rem", paddingBottom: "0.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }} title={category}>
                  {category}
                </h4>
                <ul style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                  {INDUSTRIES.filter((i) => i.category === category).sort((a, b) => a.name.localeCompare(b.name)).map((industry) => (
                    <li key={industry.slug}>
                      <Link
                        to={`/certifications/${industry.slug}`}
                        onClick={onClose}
                        style={{
                          display: "block", padding: "0.4rem 0.5rem",
                          fontSize: "0.8125rem", color: "var(--neutral-300)",
                          borderRadius: "var(--radius-sm)",
                          transition: "all var(--transition-fast)",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "var(--accent-400)";
                          e.currentTarget.style.background = "rgba(61,220,151,0.06)";
                          e.currentTarget.style.paddingLeft = "0.75rem";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "var(--neutral-300)";
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.paddingLeft = "0.5rem";
                        }}
                      >
                        {industry.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: "1.5rem", paddingTop: "1.25rem",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <Link to="/certification" onClick={onClose} style={{
              fontSize: "0.875rem", fontWeight: 600, color: "var(--accent-400)",
              display: "flex", alignItems: "center", gap: "0.375rem",
            }}>
              View All Certifications &rarr;
            </Link>
            <span style={{ fontSize: "0.75rem", color: "var(--neutral-500)" }}>
              4 levels per industry &middot; Annual renewals
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Domains mega menu — 3-column grid of cards
  return (
    <div role="menu" aria-label="Domains mega menu" style={panelStyle}>
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 2rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.75rem",
          }}
        >
          {[...DOMAINS].sort((a, b) => a.name.localeCompare(b.name)).map((domain) => (
            <Link
              key={domain.slug}
              to={`/domains/${domain.slug}`}
              onClick={onClose}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.75rem",
                padding: "0.875rem 1rem",
                borderRadius: "var(--radius-md)",
                border: "1px solid rgba(255,255,255,0.07)",
                transition: "all var(--transition-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(61,220,151,0.35)";
                e.currentTarget.style.background = "rgba(61,220,151,0.06)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div
                style={{
                  width: "38px",
                  height: "38px",
                  minWidth: "38px",
                  borderRadius: "var(--radius-md)",
                  background: "rgba(61,220,151,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent-400)",
                  fontWeight: 800,
                  fontSize: "0.9375rem",
                }}
              >
                {domain.name[0]}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    color: "var(--text-on-dark)",
                    marginBottom: "0.2rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {domain.name}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--neutral-400)",
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  } as React.CSSProperties}
                >
                  {domain.tagline}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div
          style={{
            marginTop: "1.25rem",
            paddingTop: "1rem",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link
            to="/domains"
            onClick={onClose}
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--accent-400)",
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
            }}
          >
            {t("megaMenu.viewAllDomains")} &rarr;
          </Link>
          <span style={{ fontSize: "0.75rem", color: "var(--neutral-500)" }}>
            Operational &bull; Environmental &bull; Fire &bull; Mechanical &bull; Electrical &bull; Thermal &bull; Digital &bull; Chemical &bull; Compliance &bull; Radiation &bull; Biological &bull; Structural
          </span>
        </div>
      </div>
    </div>
  );
}

/** Hook for managing mega-menu open state */
export function useMegaMenu() {
  const [openMenu, setOpenMenu] = useState<"industries" | "domains" | "certifications" | null>(null);
  return { openMenu, setOpenMenu };
}
